import { inject, Injectable } from '@angular/core';
import { first, map, Observable, switchMap, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@shs-digi-care-monorepo/frontend-lib';
import { IIroNamRequest, IroNamRequestStatus } from '@shs-digi-care-monorepo/interfaces';
import { RequestService } from './request.service';
import { TransitionReferral } from './transition-referral.interface';

export { TransitionReferral };

export interface TransitionComment {
    body: string;
    files?: string[];
    internal?: boolean;
}

export interface TransitionOptions {
    options: IroNamRequestStatus[];
}

/**
 * Service for managing IRO NAM request status transitions via API.
 */
@Injectable({
    providedIn: 'root'
})
export class TransitionService {
    protected requestEndpoint = 'iro-nam-requests';
    private readonly httpClient = inject(HttpClient);
    private readonly requestService = inject(RequestService);

    private readonly pollingIntervalMs = 2000;
    private readonly maxPollingAttempts = 30;

    /**
     * Retrieves the allowed status transitions for a request.
     */
    public getTransitionOptions(requestId: string): Observable<TransitionOptions> {
        return this.httpClient.get<TransitionOptions>(
            `${environment.apiUrl}/${this.requestEndpoint}/${requestId}/next-status-options`
        );
    }

    /**
     * Assigns a request to a user (SUBMITTED → UNDER_REVIEW).
     */
    public assign(requestId: string, assignToGid: string, comment?: TransitionComment): Observable<void> {
        return this.transitionAndPoll(requestId, IroNamRequestStatus.UNDER_REVIEW, {
            assignToGid,
            ...(comment && { comment })
        });
    }

    /**
     * Reassigns a request to another user/team (UNDER_REVIEW → UNDER_REVIEW).
     */
    public reassign(
        requestId: string,
        data: { assignToGid?: string; teamId?: string },
        comment?: TransitionComment
    ): Observable<void> {
        return this.transitionAndPoll(requestId, IroNamRequestStatus.UNDER_REVIEW, {
            ...data,
            ...(comment && { comment })
        });
    }

    /**
     * Requests feedback from the requestor (→ PENDING_CUSTOMER_ACTION).
     * A comment is required for this transition.
     */
    public requestFeedback(requestId: string, comment: TransitionComment): Observable<void> {
        return this.transitionAndPoll(requestId, IroNamRequestStatus.PENDING_CUSTOMER_ACTION, {
            comment
        });
    }

    /**
     * Refers a request out with one or more referrals (→ REFERRED_OUT).
     */
    public referOut(requestId: string, referrals: TransitionReferral[]): Observable<void> {
        return this.transitionAndPoll(requestId, IroNamRequestStatus.REFERRED_OUT, {
            referrals
        });
    }

    /**
     * Closes/resolves a request (→ CLOSED).
     */
    public resolve(requestId: string, comment?: TransitionComment): Observable<void> {
        return this.transitionAndPoll(requestId, IroNamRequestStatus.CLOSED, {
            ...(comment && { comment })
        });
    }

    /**
     * Sends a request back to the queue (UNDER_REVIEW → SUBMITTED).
     */
    public sendBack(requestId: string, teamId?: string, comment?: TransitionComment): Observable<void> {
        return this.transitionAndPoll(requestId, IroNamRequestStatus.SUBMITTED, {
            ...(teamId && { teamId }),
            ...(comment && { comment })
        });
    }

    /**
     * Submits a saved request and polls until the status has changed to SUBMITTED.
     * @param id The request ID to submit
     * @returns Observable that completes once the request status is SUBMITTED
     */
    public submitRequest(id: string): Observable<void> {
        return this.httpClient
            .post<void>(`${environment.apiUrl}/${this.requestEndpoint}/${id}/submit`, {
                skipMail: false
            })
            .pipe(switchMap(() => this.pollUntilStatus(id, IroNamRequestStatus.SUBMITTED)));
    }

    /**
     * Puts a request on hold.
     */
    public setOnHold(requestId: string, comment?: string): Observable<void> {
        return this.httpClient
            .post<void>(`${environment.apiUrl}/${this.requestEndpoint}/${requestId}/set-on-hold`, {
                ...(comment && { comment })
            })
            .pipe(switchMap(() => this.pollUntil(requestId, (request) => request.isOnHold === true)));
    }

    /**
     * Removes a request from on-hold status.
     */
    public setActive(requestId: string): Observable<void> {
        return this.httpClient
            .post<void>(`${environment.apiUrl}/${this.requestEndpoint}/${requestId}/set-active`, {})
            .pipe(switchMap(() => this.pollUntil(requestId, (request) => request.isOnHold === false)));
    }

    /**
     * Posts a status transition and polls until the request reaches the expected status.
     */
    private transitionAndPoll(
        requestId: string,
        nextStatus: IroNamRequestStatus,
        data: Record<string, unknown>
    ): Observable<void> {
        return this.httpClient
            .post<void>(`${environment.apiUrl}/${this.requestEndpoint}/${requestId}/status-transition`, {
                nextStatus,
                data
            })
            .pipe(switchMap(() => this.pollUntilStatus(requestId, nextStatus)));
    }

    /**
     * Polls the request until its status matches the expected status.
     */
    private pollUntilStatus(requestId: string, expectedStatus: IroNamRequestStatus): Observable<void> {
        return this.pollUntil(requestId, (request) => request.status === expectedStatus);
    }

    /**
     * Polls the request at a fixed interval until the predicate returns true.
     */
    private pollUntil(requestId: string, predicate: (request: IIroNamRequest) => boolean): Observable<void> {
        let attempts = 0;

        return timer(0, this.pollingIntervalMs).pipe(
            switchMap(() => {
                attempts++;
                if (attempts > this.maxPollingAttempts) {
                    throw new Error(`Status transition polling timed out after ${this.maxPollingAttempts} attempts`);
                }
                return this.requestService.getRequestById(requestId);
            }),
            first(predicate),
            map(() => void 0)
        );
    }
}
